import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ClientContactType, RootState } from "types";
import "./report-options.scss";
import { ReportType, TemplateType } from "types/report";
import api from "api";
import { useForm } from "react-hook-form";
import ComboBox from "components/controls/combo-box";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { SECTION_REPORT_GENERATE } from "../report-modal";

interface ComboBoxProps {
    Id: number;
    Description?: string;
    Icon?: string;
    showIcon?: boolean;
}

function ReportOptions({ report, setReport, setSection }: { report: ReportType | undefined; setReport: (report: ReportType) => void; setSection: (data: number) => void }) {
    // SPECIAL CASE: Not allowed to be viewed alongside other platforms.
    const YOUTUBE = 10;
    // console.log(report);
    const params: any = useParams();

    const state = useSelector((state: RootState) => state);
    const [loading, setLoading] = useState<boolean>(false);
    const [reportName, setReportName] = useState<string>(report?.Name ?? "");
    const [streamOptions, setStreamOptions] = useState<ComboBoxProps[]>(state.core.Stream.map((x) => ({ Id: x.Id, Icon: x.Description.toLowerCase() + " fa-2x", showIcon: true })));
    const [streamIds, setStreamIds] = useState<number[]>(report?.StreamIds ?? [7, 8, 9]);
    const [templateOptions, setTemplateOptions] = useState<ComboBoxProps[]>(state.reportOptions.TemplateOptions.map((x: TemplateType) => ({ Id: x.Id, Description: x.Name })));
    const [templateIds, setTemplateIds] = useState<number[]>(report?.TemplateIds ?? []);
    const [clientContactOptions] = useState<ComboBoxProps[]>(state.clientContacts.map((x: ClientContactType) => ({ Id: x.Id, Description: `${x.Name} ${x.Surname}` })));
    const [clientContactIds, setClientContactIds] = useState<number[]>(report?.RecipientIds ?? []);
    const [layoutId, setLayoutId] = useState<number>(report?.LayoutId ?? 3);

    const {
        handleSubmit,
        formState: { errors },
    } = useForm();

    const createReport = useCallback(
        async (request) => {
            setLoading(true);
            await api.report
                .create(request)
                .then((data: ReportType) => {
                    console.log(data);
                    setReport(data);
                })
                .finally(() => {
                    setLoading(false);
                    setSection(SECTION_REPORT_GENERATE);
                });
        },
        [setReport, setSection]
    );

    useEffect(() => {
        // SPECIAL CASE: Linkedin does not have Keyword Tracking
        const KEYWORD_TRACKING = 5;
        const LINKEDIN = 11;
        if (state.project.ProjectTypeId === KEYWORD_TRACKING) setStreamOptions(state.core.Stream.filter((x) => x.Id !== LINKEDIN).map((x) => ({ Id: x.Id, Icon: x.Description.toLowerCase() + " fa-2x", showIcon: true })));
    }, [state.core.Stream, state.project.ProjectTypeId]);

    useEffect(() => {
        setTemplateOptions(state.reportOptions.TemplateOptions.filter((x) => x.LayoutId === layoutId).map((x: TemplateType) => ({ Id: x.Id, Description: x.Name })));
    }, [state.reportOptions.TemplateOptions, layoutId]);

    const onSubmit = (data: any) => {
        if (streamIds.length > 1 && streamIds.includes(YOUTUBE)) {
            toast.warn("Sorry Youtube cannot be selected alongside other streams");
        }
        console.log(streamIds);
        var request = {
            Id: report?.Id ?? undefined,
            DateCreated: report?.DateCreated ?? undefined,
            BinIds: [state.selectedBin ? state.selectedBin : params.projectId],
            ClientId: params.clientId,
            ContactId: state.user.Id,
            LayoutId: layoutId,
            Name: reportName,
            RecipientIds: clientContactIds,
            StreamIds: streamIds,
            TemplateIds: templateIds,
        };
        if (errors) {
            toast.warn(errors);
        }

        createReport(request);
    };

    const toggleStream = (id: number) => {
        if (id === YOUTUBE && !streamIds.includes(YOUTUBE)) {
            setStreamIds([YOUTUBE]);
        } else if (id !== YOUTUBE && streamIds.includes(YOUTUBE)) {
            setStreamIds([id]);
        } else {
            if (streamIds.includes(id)) {
                setStreamIds(streamIds.filter((x) => x !== id));
            } else {
                setStreamIds([...streamIds, id]);
            }
        }
    };

    const toggleArray = (id: number, arr: number[], set: Dispatch<SetStateAction<number[]>>) => {
        if (arr.includes(id)) {
            set(arr.filter((x) => x !== id));
        } else {
            set([...arr, id]);
        }
    };

    return (
        <div id={"report-options"} className="options-container">
            <h2>{"REPORT OPTIONS"}</h2>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="input-group">
                    <label> Report Name </label>
                    <input type={"text"} value={reportName} placeholder={"Report Name"} onChange={(e) => setReportName(e.currentTarget.value)} />
                </div>
                <div className="input-group">
                    <label> Layout </label>
                    <select defaultValue={layoutId} onChange={(e) => setLayoutId(parseInt(e.currentTarget.value))}>
                        <option value="" disabled selected>
                            Select Layout
                        </option>
                        {state.reportOptions.LayoutOptions.map((item, idx) => {
                            return (
                                <option key={idx} value={item.Id}>
                                    {item.Description}
                                </option>
                            );
                        })}
                    </select>
                </div>
                <div className="filters-section streams">
                    <p className="filters-section-heading">Streams</p>
                    <ComboBox name="report-stream" items={streamOptions} selected={streamIds} toggleItem={toggleStream} />
                </div>
                <div className="filters-section templates">
                    <p className="filters-section-heading">Templates</p>
                    <ComboBox name="report-template" items={templateOptions.filter((x) => x.Id !== 7 && x.Id !== 4)} selected={templateIds} toggleItem={(data) => toggleArray(data, templateIds, setTemplateIds)} />
                </div>

                <div className="filters-section contacts">
                    <p className="filters-section-heading">Contacts</p>
                    <ComboBox name="report-contact" items={clientContactOptions} selected={clientContactIds} toggleItem={(data) => toggleArray(data, clientContactIds, setClientContactIds)} />
                </div>

                <button className="button" type="submit">
                    {loading ? <i className="fal fa-spinner fa-spin"></i> : <span>SUBMIT</span>}
                </button>
            </form>
        </div>
    );
}

export default ReportOptions;
